// app/ningenie/lib/ningenie-seed.ts
// baby: default dataset for NinGenie to hydrate Firestore when empty

import {
  NinGenieSnapshot,
  NinGenieAccount,
  NinGenieBudget,
  NinGenieCategory,
  NinGenieTransaction,
  NinGeniePersistedDocument,
  NinGenieUserProfile
} from './ningenie-types'

const nowIso = () => new Date().toISOString()

const accounts: NinGenieAccount[] = [
  {
    id: 'account-paytm-wallet',
    name: 'Paytm Wallet',
    type: 'wallet',
    institution: 'Paytm',
    initialBalance: 0,
    balance: -38289.2,
    currency: 'INR',
    iconKey: 'paytm',
    color: '#2563eb',
    lastUpdated: nowIso()
  },
  {
    id: 'account-card',
    name: 'Card (ICICI)',
    type: 'card',
    institution: 'ICICI',
    initialBalance: 2500000,
    balance: 2638289.2,
    currency: 'INR',
    iconKey: 'card',
    color: '#1c4b5a',
    lastUpdated: nowIso()
  },
  {
    id: 'account-cash',
    name: 'Cash',
    type: 'cash',
    initialBalance: 500,
    balance: -289.2,
    currency: 'INR',
    iconKey: 'cash',
    color: '#f97316',
    lastUpdated: nowIso()
  },
  {
    id: 'account-savings',
    name: 'Savings',
    type: 'bank',
    institution: 'SBI',
    initialBalance: 50000,
    balance: 77289.2,
    currency: 'INR',
    iconKey: 'bank',
    color: '#0f172a',
    lastUpdated: nowIso()
  },
  {
    id: 'account-upi-icici',
    name: 'UPI1(7829272920@okicici)',
    type: 'upi',
    institution: 'ICICI',
    initialBalance: 10000,
    balance: 14289.2,
    currency: 'INR',
    iconKey: 'upi',
    color: '#0891b2',
    lastUpdated: nowIso()
  },
  {
    id: 'account-phonepe',
    name: 'PhonePe',
    type: 'upi',
    institution: 'PhonePe',
    initialBalance: 5000,
    balance: 14289.2,
    currency: 'INR',
    iconKey: 'phonepe',
    color: '#7c3aed',
    lastUpdated: nowIso()
  },
  {
    id: 'account-gpay',
    name: 'GPay',
    type: 'upi',
    institution: 'Google',
    initialBalance: 0,
    balance: 0,
    currency: 'INR',
    iconKey: 'gpay',
    color: '#38bdf8',
    lastUpdated: nowIso()
  },
  {
    id: 'account-ningenie-upi',
    name: 'NINJAPAY UPI',
    type: 'upi',
    institution: 'NINJAPAY',
    initialBalance: 10000,
    balance: 14289.2,
    currency: 'INR',
    iconKey: 'ninjapay',
    color: '#10b981',
    lastUpdated: nowIso()
  },
  {
    id: 'account-earnings',
    name: 'NINJAPAY (My Earnings)',
    type: 'virtual',
    institution: 'NINJAPAY',
    initialBalance: 750000,
    balance: 814289.2,
    currency: 'INR',
    iconKey: 'earnings',
    color: '#0ea5e9',
    lastUpdated: nowIso()
  }
]

const categories: NinGenieCategory[] = [
  {
    id: 'cat-gifts',
    name: 'Gifts',
    type: 'expense',
    iconKey: 'gift',
    color: '#f97316'
  },
  {
    id: 'cat-travel',
    name: 'Travel',
    type: 'expense',
    iconKey: 'travel',
    color: '#1d4ed8'
  },
  {
    id: 'cat-fuel',
    name: 'Fuel',
    type: 'expense',
    iconKey: 'fuel',
    color: '#f43f5e'
  },
  {
    id: 'cat-undefined-expense',
    name: 'Undefined',
    type: 'expense',
    iconKey: 'question',
    color: '#6b7280'
  },
  {
    id: 'cat-gifts-income',
    name: 'Gifts',
    type: 'income',
    iconKey: 'gift',
    color: '#22c55e'
  },
  {
    id: 'cat-travel-income',
    name: 'Travel',
    type: 'income',
    iconKey: 'travel',
    color: '#38bdf8'
  },
  {
    id: 'cat-fuel-income',
    name: 'Fuel',
    type: 'income',
    iconKey: 'fuel',
    color: '#a855f7'
  },
  {
    id: 'cat-undefined-income',
    name: 'Undefined',
    type: 'income',
    iconKey: 'question',
    color: '#475569'
  }
]

const budgets: NinGenieBudget[] = [
  {
    id: 'budget-shopping',
    categoryId: 'cat-gifts',
    limit: 1000,
    spent: 1200,
    periodLabel: 'March 24',
    period: 'monthly',
    createdAt: nowIso(),
    updatedAt: nowIso()
  }
]

const transactions: NinGenieTransaction[] = [
  {
    id: 'txn-fuel-mar18',
    type: 'expense',
    accountId: 'account-upi-icici',
    counterpartyAccountId: null,
    categoryId: 'cat-fuel',
    amount: 12,
    currency: 'INR',
    note: 'Travel',
    tags: [],
    timestamp: '2024-03-18T09:30:00.000Z',
    createdAt: nowIso()
  },
  {
    id: 'txn-gifts-mar18',
    type: 'income',
    accountId: 'account-earnings',
    counterpartyAccountId: null,
    categoryId: 'cat-gifts-income',
    amount: 20,
    currency: 'INR',
    note: 'Gifts',
    tags: [],
    timestamp: '2024-03-18T11:30:00.000Z',
    createdAt: nowIso()
  },
  {
    id: 'txn-fuel-mar17',
    type: 'income',
    accountId: 'account-earnings',
    counterpartyAccountId: null,
    categoryId: 'cat-fuel-income',
    amount: 27920.72,
    currency: 'INR',
    note: 'Transfer',
    tags: [],
    timestamp: '2024-03-17T09:30:00.000Z',
    createdAt: nowIso()
  },
  {
    id: 'txn-travel-mar17',
    type: 'expense',
    accountId: 'account-upi-icici',
    counterpartyAccountId: null,
    categoryId: 'cat-travel',
    amount: 12.9,
    currency: 'INR',
    note: 'Travel',
    tags: [],
    timestamp: '2024-03-17T13:30:00.000Z',
    createdAt: nowIso()
  },
  {
    id: 'txn-gifts-mar17',
    type: 'income',
    accountId: 'account-earnings',
    counterpartyAccountId: null,
    categoryId: 'cat-gifts-income',
    amount: 20.1,
    currency: 'INR',
    note: 'Gifts',
    tags: [],
    timestamp: '2024-03-17T14:30:00.000Z',
    createdAt: nowIso()
  }
]

const totalIncome = transactions
  .filter(txn => txn.type === 'income')
  .reduce((acc, txn) => acc + txn.amount, 0)

const totalExpense = transactions
  .filter(txn => txn.type === 'expense')
  .reduce((acc, txn) => acc + txn.amount, 0)

export const NINGENIE_SEED: NinGenieSnapshot = {
  summary: {
    totalIncome,
    totalExpense,
    totalBalance: totalIncome - totalExpense,
    periodLabel: 'March 24'
  },
  accounts,
  categories,
  budgets,
  transactions,
  updatedAt: nowIso()
}

export const NINGENIE_MOCK_USER: NinGenieUserProfile = {
  mainId: 'mock-main-uuid-001',
  phone: '+919876543210',
  email: 'ningenie-mock@nin.ai'
}

export const NINGENIE_SEED_DOCUMENT: NinGeniePersistedDocument = {
  snapshot: NINGENIE_SEED,
  user: NINGENIE_MOCK_USER,
  metadataVersion: 1,
  updatedAt: nowIso()
}

